import { SessionSummary } from '@bioliberty/lifemind';
import { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { SortableHeader } from '../../components/DataTable/SortableHeader';
import { Tile } from '../../components/Tile';
import { formatSecondsString } from '../../util/date';
import { MonthlySessionsSummary, useMonthlySessions } from '../hooks/useMonthlySessions';

export interface MonthlySessionsTileProps {
  sessions: SessionSummary[];
}

export function MonthlySessionsTile({ sessions }: MonthlySessionsTileProps) {
  const monthlySessions = useMonthlySessions(sessions);

  return (
    <Tile title="Monthly Sessions">
      <DataTable
        rows={monthlySessions}
        columns={columnDefinitions}
        initialSorting={[
          { id: 'clinic', desc: false },
          { id: 'year', desc: true },
          { id: 'month', desc: true }
        ]}
        stickyHeader
        className="mt-4 min-w-[2000px]"
      />
    </Tile>
  );
}

// The thresholds for showing patient usage indicators
const lowMonthlyPatientsUsageThreshold = 5;
const highMonthlyPatientsUsageThreshold = 9;

const columnDefinitions: ColumnDef<MonthlySessionsSummary>[] = [
  {
    accessorKey: 'clinic',
    header: ({ column }) => <SortableHeader column={column}>Clinic</SortableHeader>
  },
  {
    accessorKey: 'year',
    header: ({ column }) => <SortableHeader column={column}>Year</SortableHeader>
  },
  {
    accessorKey: 'month',
    header: ({ column }) => <SortableHeader column={column}>Month</SortableHeader>,
    cell: ({ row }) => {
      const month: number = row.getValue('month');
      const date = new Date();
      date.setDate(1);
      date.setMonth(month);
      return date.toLocaleString('en-US', { month: 'long' });
    }
  },
  {
    accessorKey: 'patients',
    header: ({ column }) => <SortableHeader column={column}>Patients</SortableHeader>,
    cell: ({ row }) => {
      const value: number = row.getValue('patients');
      return (
        <div className="flex items-center gap-2">
          {value < lowMonthlyPatientsUsageThreshold && <AlertTriangle className="h-4 w-4 text-red-500" />}
          {value > highMonthlyPatientsUsageThreshold && <CheckCircle className="h-4 w-4 text-green-500" />}
          <span>{value}</span>
        </div>
      );
    }
  },
  {
    accessorKey: 'sessions',
    header: ({ column }) => <SortableHeader column={column}>Sessions</SortableHeader>
  },
  {
    accessorKey: 'totalReps',
    header: ({ column }) => <SortableHeader column={column}>Reps</SortableHeader>
  },
  {
    accessorKey: 'totalTimeTaken',
    header: ({ column }) => <SortableHeader column={column}>Time on task</SortableHeader>,
    cell: ({ row }) => {
      return formatSecondsString(row.getValue('totalTimeTaken'));
    }
  },
  {
    accessorKey: 'repsPerPatient',
    header: ({ column }) => <SortableHeader column={column}>Reps/patient</SortableHeader>,
    cell: ({ row }) => {
      const value: number = row.getValue('repsPerPatient');
      return value.toFixed(2);
    }
  },
  {
    accessorKey: 'repsPerSession',
    header: ({ column }) => <SortableHeader column={column}>Reps/session</SortableHeader>,
    cell: ({ row }) => {
      const value: number = row.getValue('repsPerSession');
      return value.toFixed(2);
    }
  },
  {
    accessorKey: 'timeTakenPerPatient',
    header: ({ column }) => <SortableHeader column={column}>Time on task/patient</SortableHeader>,
    cell: ({ row }) => {
      return formatSecondsString(row.getValue('timeTakenPerPatient'));
    }
  },
  {
    accessorKey: 'timeTakenPerSession',
    header: ({ column }) => <SortableHeader column={column}>Time on task/session</SortableHeader>,
    cell: ({ row }) => {
      return formatSecondsString(row.getValue('timeTakenPerSession'));
    }
  },
  {
    accessorKey: 'sessionsPerPatient',
    header: ({ column }) => <SortableHeader column={column}>Sessions/patient</SortableHeader>,
    cell: ({ row }) => {
      const value: number = row.getValue('sessionsPerPatient');
      return value.toFixed(2);
    }
  }
];
